/**
 * Reusable "Other details" field for inventory: label + value rows stored as metadata.
 * Value is an array of { label, value }. Use in project/configuration forms.
 */
import React from 'react';
import { Box, TextField, Button, IconButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { SectionHeader, SECTION_COLORS } from '../../utils/inventory_helper';

const fieldSx = (accentColor = SECTION_COLORS.purple) => ({
  '& .MuiInputBase-root': { height: 40, fontSize: 12, borderRadius: 1.5 },
  '& .MuiInputLabel-root': { fontSize: 12, color: SECTION_COLORS.sub, '&.Mui-focused': { color: accentColor } },
  '& .MuiOutlinedInput-root': {
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: accentColor, borderWidth: 1.5 },
  },
  '& .MuiInputBase-input::placeholder': { fontSize: 12, opacity: 0.6 },
});

/**
 * @param {Object} props
 * @param {{ label: string, value: string }[]} props.value - Array of label/value rows (e.g. for metadata).
 * @param {function({ label: string, value: string }[]): void} props.onChange - Called when the list changes.
 * @param {string} [props.title='Other details']
 * @param {string} [props.subtitle='Add any label and value (stored as metadata)']
 * @param {string} [props.accent='other'] - SectionHeader accent key.
 * @param {boolean} [props.showSectionHeader=true] - When false, do not render SectionHeader (e.g. when parent provides it).
 * @param {object} [props.sx] - Optional MUI sx for the root Box.
 */
const InventoryOtherDetails = ({
  value = [{ label: '', value: '' }],
  onChange,
  title = 'Other details',
  subtitle = 'Add any label and value (stored as metadata)',
  accent = 'other',
  showSectionHeader = true,
  sx = {},
}) => {
  const list = Array.isArray(value) && value.length > 0 ? value : [{ label: '', value: '' }];

  const updateAt = (index, field, text) => {
    const next = list.map((row, i) => (i === index ? { ...row, [field]: text } : row));
    onChange(next);
  };

  const removeAt = (index) => {
    const next = list.filter((_, i) => i !== index);
    if (next.length === 0) next.push({ label: '', value: '' });
    onChange(next);
  };

  const addRow = () => {
    onChange([...list, { label: '', value: '' }]);
  };

  return (
    <Box sx={{ ...sx }}>
      {showSectionHeader && <SectionHeader title={title} subtitle={subtitle} accent={accent} />}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {list.map((row, index) => (
          <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <TextField
              placeholder="Label"
              value={row.label ?? ''}
              onChange={(e) => updateAt(index, 'label', e.target.value)}
              sx={{ ...fieldSx(), flex: 1 }}
              size="small"
            />
            <TextField
              placeholder="Value"
              value={row.value ?? ''}
              onChange={(e) => updateAt(index, 'value', e.target.value)}
              sx={{ ...fieldSx(), flex: 1 }}
              size="small"
            />
            <IconButton
              size="small"
              onClick={() => removeAt(index)}
              disabled={list.length <= 1}
              sx={{ color: SECTION_COLORS.sub, mt: 0.5 }}
              aria-label="Remove row"
            >
              <DeleteOutlineIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        ))}
        <Button
          startIcon={<AddIcon sx={{ fontSize: 16 }} />}
          onClick={addRow}
          size="small"
          sx={{ textTransform: 'none', fontSize: 12, color: SECTION_COLORS.purple, alignSelf: 'flex-start' }}
        >
          Add row
        </Button>
      </Box>
    </Box>
  );
};

export default InventoryOtherDetails;
